import { formatDate } from '@/utils/formatTime'

/** 骑手开工认证状态（与后端 RiderCertProfile.STATUS_* 一致） */
export const RIDER_CERT_STATUS = {
  NONE: 'none',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  AMENDING: 'amending'
} as const

export type RiderCertStatus = (typeof RIDER_CERT_STATUS)[keyof typeof RIDER_CERT_STATUS]

const CERT_STATUS_LABEL: Record<string, string> = {
  none: '未完成',
  pending: '待审',
  approved: '已通过',
  rejected: '已驳回',
  amending: '修改中'
}

/** Element Plus Tag 类型，便于列表一眼区分状态 */
const CERT_STATUS_TAG: Record<string, '' | 'success' | 'warning' | 'info' | 'danger'> = {
  none: 'info',
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  amending: 'warning'
}

/** 认证状态 → 中文（未知值原样返回，便于排查） */
export function certStatusLabel(status?: string | null): string {
  if (!status) return '未完成'
  return CERT_STATUS_LABEL[status] ?? status
}

/** 认证状态 → Tag 颜色 */
export function certStatusTagType(status?: string | null) {
  if (!status) return 'info' as const
  return CERT_STATUS_TAG[status] ?? ('info' as const)
}

/** 运单策略（与后端 strategy 字段一致） */
export const WAYBILL_STRATEGY = {
  FREE_POOL: 'free_pool',
  ORG_DISPATCH: 'org_dispatch'
} as const

const WAYBILL_STRATEGY_LABEL: Record<string, string> = {
  free_pool: '自由抢单',
  org_dispatch: '机构派单'
}

/** 运单状态（与后端 status 字段一致） */
export const WAYBILL_STATUS = {
  ACCEPTED: 'accepted',
  GRABBED: 'grabbed',
  ARRIVED_PICK: 'arrived_pick',
  PICKED: 'picked',
  DELIVERING: 'delivering',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
} as const

const WAYBILL_STATUS_LABEL: Record<string, string> = {
  accepted: '待接单',
  grabbed: '已接单',
  arrived_pick: '已到取货点',
  picked: '已取货',
  delivering: '配送中',
  completed: '已完成',
  cancelled: '已取消'
}

const WAYBILL_STATUS_TAG: Record<string, '' | 'success' | 'warning' | 'info' | 'danger'> = {
  accepted: 'warning',
  grabbed: 'info',
  arrived_pick: 'info',
  picked: 'info',
  delivering: 'warning',
  completed: 'success',
  cancelled: 'danger'
}

/** 运单策略 → 中文 */
export function waybillStrategyLabel(strategy?: string | null): string {
  if (!strategy) return '—'
  return WAYBILL_STRATEGY_LABEL[strategy] ?? strategy
}

/** 运单状态 → 中文（运营端；跑腿购买点差异见 waybillStatusLabelForBiz） */
export function waybillStatusLabel(status?: string | null, bizType?: string | null): string {
  if (!status) return '—'
  const biz = (bizType || '').toLowerCase()
  if (status === 'arrived_pick' && biz === 'errand_buy') return '已到购买点'
  if (status === 'picked' && biz === 'errand_buy') return '已购妥'
  return WAYBILL_STATUS_LABEL[status] ?? status
}

/** 运单状态 → Tag 颜色 */
export function waybillStatusTagType(status?: string | null) {
  if (!status) return 'info' as const
  return WAYBILL_STATUS_TAG[status] ?? ('info' as const)
}

/** 筛选用：全部已知运单状态（中文展示，值仍为英文） */
export const WAYBILL_STATUS_FILTER_OPTIONS = Object.entries(WAYBILL_STATUS_LABEL).map(
  ([value, label]) => ({ value, label })
)

const CURRENCY_UNIT_LABEL: Record<string, string> = {
  CNY: '元',
  RMB: '元',
  USD: '美元',
  EUR: '欧元',
  HKD: '港元',
  JPY: '日元'
}

/** ISO 货币码 → 中文单位（默认人民币元） */
export function currencyUnitLabel(currency?: string | null): string {
  if (!currency || !currency.trim()) return '元'
  const key = currency.trim().toUpperCase()
  return CURRENCY_UNIT_LABEL[key] ?? currency
}

/** 金额 + 中文单位，如「4 元」「12.50 元」 */
export function formatMoneyAmount(
  amount?: number | string | null,
  currency?: string | null
): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  const n = Number(amount)
  if (!Number.isFinite(n)) return '—'
  const text = Number.isInteger(n) ? String(n) : n.toFixed(2)
  return `${text} ${currencyUnitLabel(currency)}`
}

/** 时间戳 / ISO / LocalDateTime 数组 → 可读时间，空则 — */
export function formatCapacityDateTime(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '—'
  const text = formatDate(value)
  return text || '—'
}

/** 处罚操作方 actorRole（后端枚举，仅展示层翻译） */
const PENALTY_ACTOR_ROLE_LABEL: Record<string, string> = {
  platform: '平台',
  org: '机构',
  system: '系统'
}

export function penaltyActorRoleLabel(role?: string | null): string {
  if (!role) return '—'
  return PENALTY_ACTOR_ROLE_LABEL[role] ?? role
}
